import { Form } from 'components/form'
import { useAlert } from 'contexts/alert'
import { IRecipeExecutionLogDTO } from 'data/dtos/automation/i-recipe-execution-log-dto'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import { SubmitHandler, useForm } from 'react-hook-form'
import { useHistory, useParams } from 'react-router-dom'
import api from 'services/api'

import { ajvResolver } from '@hookform/resolvers/ajv'
import RecipeExecutionLogSchema, { IRecipeExecutionLogFormData } from 'schema/automation/recipe-execution-log'
import { useStyles } from './styles'

interface IRouteParams {
  id: string
}

const RecipeExecutionLogForm: React.FC = () => {
  const [isLoading, setIsLoading] = useState(false)
  const [readonly, setReadonly] = useState(false)
  const [recipesA, setrecipesA] = useState([])
  const { closeAlert, openAlert } = useAlert()
  const params = useParams<IRouteParams>()
  const firstInputElement = useRef(null)
  const classes = useStyles()
  const history = useHistory()

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { errors },
    control,
    setFocus,
  } = useForm<IRecipeExecutionLogFormData>({
    resolver: ajvResolver(RecipeExecutionLogSchema),
    defaultValues: {
      recipeId: '',
      executionFinalStatus: '',
      billingReference: '',
      billingAmount: 0,
      billingDetail: '',
    },
  })

  const handleChange = (formField: any) => closeAlert()

  // main data load
  useEffect(() => {
    async function loadData() {
      const { id } = params

      // form data
      await api
        .get(`/recipes-execution-logs/${id}`)
        .then((response) => {
          const { data } = response.data

          const recipeExecutionLogResult = {
            recipeId: data.recipeId.id,
            executionDateTimeStart: data.executionDateTimeStart,
            executionDateTimeEnd: data.executionDateTimeEnd,
            executionFinalStatus: data.executionFinalStatus,
            executionOutput: data.executionOutput,
            billingReference: data.billingReference,
            billingAmount: data.billingAmount,
            billingDetail: data.billingDetail,
          }

          return recipeExecutionLogResult
        })
        .then((recipeExecutionLogResult: IRecipeExecutionLogDTO) => {
          reset(recipeExecutionLogResult as any)
        })
        .catch((error) => {
          console.log(error)
          return error
        })
    }

    if (params.id) {
      loadData()
      setReadonly(true)
    }
  }, [params, params.id])

  // data save
  const onSubmit: SubmitHandler<IRecipeExecutionLogFormData> = useCallback(async (data) => {
    setIsLoading(true)

    const payLoad: IRecipeExecutionLogFormData = {
      id: data.id,
      recipeId: data.recipeId,
      executionDateTimeStart: data.executionDateTimeStart,
      executionDateTimeEnd: data.executionDateTimeEnd,
      executionFinalStatus: data.executionFinalStatus,
      executionOutput: data.executionOutput,
      billingReference: data.billingReference,
      billingAmount: data.billingAmount,
      billingDetail: data.billingDetail,
    }

    if (params.id) {
      const { id } = params

      payLoad.id = id

      await api
        .put(`/recipes-execution-logs`, payLoad)
        .then(() => {
          openAlert('Cliente atualizado com sucesso!', 'success')
          history.push('/recipes-execution-logs')
        })
        .catch((error) => {
          openAlert(error?.response?.data?.data?.message ?? 'Ops! Algo inesperado aconteceu!', 'error')
        })
        .finally(() => setIsLoading(false))
    } else {
      await api
        .post('/recipes-execution-logs', payLoad)
        .then(() => {
          openAlert('Dado cadastrado com sucesso!', 'success')
          reset()
        })
        .catch((error) => {
          openAlert(error?.response?.data?.data?.message ?? 'Ops! Algo inesperado aconteceu!', 'error')
        })
        .finally(() => setIsLoading(false))
    }
  }, [])

  return (
    <Form.Root onHandleSubmit={handleSubmit(onSubmit)}>
      <Form.Info
        title='Log de Execução de Receitas'
        subTitle='Dados coletados sobre os logs de execução das receitas de automação.'
      />

      <Form.Body>
        <Form.Select
          label='Receita'
          error={!!errors.recipeId}
          errorMessage={errors?.recipeId?.message}
          disabled={isLoading || readonly}
          sizing={{ xs: 12, sm: 12, md: 3, lg: 3, xl: 3 }}
          name='recipeId'
          rules={{
            onChange: (e) => {
              setValue('recipeId', e.target.value)
              handleChange(e)
            },
          }}
          register={register}
          value={`${watch().recipeId}`}
          options={recipesA}
        />

        <Form.Input
          label='Início Execução'
          error={!!errors.executionDateTimeStart}
          errorMessage={errors?.executionDateTimeStart?.message}
          disabled={isLoading || readonly}
          sizing={{ xs: 12, sm: 12, md: 3, lg: 3, xl: 3 }}
          maxLength={20}
          name='executionDateTimeStart'
          rules={{ onChange: (e) => handleChange(e) }}
          register={register}
          type='date'
        />

        <Form.Input
          label='Término Execução'
          error={!!errors.executionDateTimeEnd}
          errorMessage={errors?.executionDateTimeEnd?.message}
          disabled={isLoading || readonly}
          sizing={{ xs: 12, sm: 12, md: 3, lg: 3, xl: 3 }}
          maxLength={20}
          name='executionDateTimeEnd'
          rules={{ onChange: (e) => handleChange(e) }}
          register={register}
          type='date'
        />

        <Form.Input
          label='Status da Execução'
          error={!!errors.executionFinalStatus}
          errorMessage={errors?.executionFinalStatus?.message}
          disabled={isLoading || readonly}
          sizing={{ xs: 12, sm: 12, md: 3, lg: 3, xl: 3 }}
          maxLength={20}
          name='executionFinalStatus'
          rules={{ onChange: (e) => handleChange(e) }}
          register={register}
        />

        <Form.Input
          label='Saída da Execução'
          error={!!errors.executionOutput}
          errorMessage={errors?.executionOutput?.message}
          disabled={isLoading || readonly}
          sizing={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}
          maxLength={1024}
          name='executionOutput'
          rules={{ onChange: (e) => handleChange(e) }}
          register={register}
        />

        <Form.Input
          label='Ano/Mês de Cobrança'
          error={!!errors.billingReference}
          errorMessage={errors?.billingReference?.message}
          disabled={isLoading || readonly}
          sizing={{ xs: 12, sm: 12, md: 6, lg: 6, xl: 6 }}
          maxLength={6}
          name='billingReference'
          rules={{ onChange: (e) => handleChange(e) }}
          register={register}
        />

        <Form.Input
          label='Valor Cobrado'
          error={!!errors.billingAmount}
          errorMessage={errors?.billingAmount?.message}
          disabled={isLoading || readonly}
          sizing={{ xs: 12, sm: 12, md: 6, lg: 6, xl: 6 }}
          maxLength={10}
          name='billingAmount'
          rules={{ onChange: (e) => handleChange(e) }}
          register={register}
          type='number'
        />

        <Form.Input
          label='Detalhamento do Consumo'
          error={!!errors.billingDetail}
          errorMessage={errors?.billingDetail?.message}
          disabled={isLoading || readonly}
          sizing={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}
          maxLength={4096}
          name='billingDetail'
          rules={{ onChange: (e) => handleChange(e) }}
          register={register}
        />
      </Form.Body>

      <Form.Divisor />

      <Form.Footer>
        <Form.Button
          label='SALVAR'
          type='submit'
          variant='contained'
          isLoading={isLoading}
        />
        <Form.Button
          label='CANCELAR'
          type='cancel'
          variant='text'
          isLoading={isLoading}
        />
      </Form.Footer>
    </Form.Root>
  )
}

export default RecipeExecutionLogForm
